import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save } from 'lucide-react'
import { besuche as besucheApi, kunden as kundenApi, benutzer as benutzerApi } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { TopBar } from '../../components/layout/TopBar'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import type { Besuch } from '../../types'

const schema = z.object({
  kunde_id: z.string().min(1, 'Kunde erforderlich'),
  datum: z.string().min(1, 'Datum erforderlich'),
  uhrzeit_start: z.string().optional(),
  uhrzeit_ende: z.string().optional(),
  typ: z.enum(['service', 'verkauf', 'installation', 'garantie', 'demo', 'notfall']),
  status: z.enum(['geplant', 'unterwegs', 'abgeschlossen', 'abgesagt']),
  besprochene_punkte: z.string().optional(),
  zusammenfassung: z.string().optional(),
  was_gemacht: z.string().optional(),
  naechste_schritte: z.string().optional(),
  techniker: z.string().min(1, 'Accountmanager erforderlich'),
})

type FormData = z.infer<typeof schema>

export function BesuchFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const { data: kundenList } = useQuery({ queryKey: ['kunden'], queryFn: () => kundenApi.list() })
  const { data: benutzerList } = useQuery({ queryKey: ['benutzer'], queryFn: () => benutzerApi.list() })

  const { register, handleSubmit, control, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      kunde_id: searchParams.get('kunde_id') ?? '',
      datum: new Date().toISOString().slice(0, 10),
      typ: 'service',
      status: 'geplant',
      techniker: user?.name ?? '',
    },
  })

  const mutation = useMutation({
    mutationFn: (data: Partial<Besuch>) => besucheApi.create(data),
    onSuccess: (besuch) => {
      qc.invalidateQueries({ queryKey: ['besuche'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(`/besuche/${besuch.id}`)
    },
  })

  return (
    <>
      <TopBar title="Besuch planen" backTo="/besuche" />
      <div className="px-4 py-3 pb-8">
        <form onSubmit={handleSubmit((d) => mutation.mutate(d as unknown as Partial<Besuch>))} className="space-y-4">
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">Grundinformationen</h3>
            <div className="space-y-4">
              <Select
                label="Kunde"
                required
                options={(kundenList ?? []).map((k) => ({ value: k.id, label: k.firma ?? k.name }))}
                placeholder="Kunde auswählen"
                error={errors.kunde_id?.message}
                {...register('kunde_id')}
              />
              <Input label="Datum" type="date" required error={errors.datum?.message} {...register('datum')} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Von" type="time" {...register('uhrzeit_start')} />
                <Input label="Bis" type="time" {...register('uhrzeit_ende')} />
              </div>
              <Select
                label="Besuchstyp"
                required
                options={[
                  { value: 'service', label: 'Service / Wartung' },
                  { value: 'verkauf', label: 'Verkauf' },
                  { value: 'installation', label: 'Installation' },
                  { value: 'garantie', label: 'Garantie' },
                  { value: 'demo', label: 'Demo / Vorführung' },
                  { value: 'notfall', label: 'Notfall' },
                ]}
                {...register('typ')}
              />
              <Select
                label="Status"
                options={[
                  { value: 'geplant', label: 'Geplant' },
                  { value: 'unterwegs', label: 'Unterwegs' },
                  { value: 'abgeschlossen', label: 'Abgeschlossen' },
                  { value: 'abgesagt', label: 'Abgesagt' },
                ]}
                {...register('status')}
              />
              <Controller
                name="techniker"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Accountmanager"
                    required
                    error={errors.techniker?.message}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    options={(benutzerList ?? []).filter(b => !!b.aktiv).map(b => ({ value: b.name, label: b.name }))}
                    placeholder="Person auswählen"
                  />
                )}
              />
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">Besuchsraport</h3>
            <div className="space-y-4">
              <Textarea
                label="Besprochene Punkte"
                placeholder="Was wurde besprochen? Kundenwünsche, Probleme, Vereinbarungen..."
                rows={6}
                {...register('besprochene_punkte')}
              />
              <Textarea
                label="Was wurde gemacht / erledigt"
                placeholder="Durchgeführte Arbeiten, Reparaturen, Installationen..."
                rows={3}
                {...register('was_gemacht')}
              />
              <Textarea
                label="Nächste Schritte / Follow-up"
                placeholder="Offene Punkte, Folgearbeiten, Bestellungen..."
                rows={3}
                {...register('naechste_schritte')}
              />
            </div>
          </Card>

          {mutation.error && (
            <p className="text-sm text-red-600 text-center">{mutation.error.message}</p>
          )}

          <Button type="submit" fullWidth size="lg" icon={<Save size={18} />} loading={mutation.isPending}>
            Besuch speichern
          </Button>
        </form>
      </div>
    </>
  )
}
